import { Formik } from "formik";
import { useCustomerAccount } from "../../AccountProvider";
import React, { useState, useContext, useRef } from "react";
import { LoadingContext } from "../../LoadingProvider";
import { isEmpty } from "lodash";
import { CSSTransition } from "react-transition-group";
import PasswordRequirementsOutline from "../../../../components/common/PasswordRequirementOutline/PasswordRequirementsOutline";
import { FieldError } from "../../../common/fields";
import { getOtpOptions } from "../../../../api/custAccountApi";
import { handleApiError } from "../../../../utils/errors"
import {isPasswordFieldValid, longestCommonPasswordSubstring} from "../../../../utils/validators"
import { isUserB2B, isNativeApp } from "../../../../utils"
import { PasswordEditOtpModal } from "./PasswordEditOtpModal";
import {Button, ButtonGroup} from "@one-thd/sui-atomic-components";
import Input from "../../../common/fields/base/Input";
import classNames from "classnames";
import {MESSAGE} from "../../../../constants";

const PasswordEdit = ({ onDoneEditing }) => {
  const {email} = useCustomerAccount();
  const { showLoading, hideLoading } = useContext(LoadingContext);

  const [showPassword, setShowPassword] = useState(false);
  const [isNewPasswordFocus, setIsNewPasswordFocus] = useState(false);
  // 1 time flag to prevent new password  guidelines from
  // displaying until user has focused on form
  const [hasNewPasswordBeenFocused, setHasNewPasswordBeenFocused] = useState(false);
  // state for OTP-based password update
  const [showPasswordEditOtpModal, setShowPasswordEditOtpModal] = useState(false);
  const newPassword = useRef("");
  const otpOptions = useRef([]);
  const otpOptionsEncryptedPayload = useRef(null);

  const closePasswordEditOtpModal = () => setShowPasswordEditOtpModal(false);

  const onFocus = () => {
    setIsNewPasswordFocus(true);
    setHasNewPasswordBeenFocused(true);
  }

  return (
    <Formik
      initialValues={{
        currentPassword: "",
        newPassword: "",
      }}
      validate={isPasswordFieldValid(email)}
      onSubmit={
        (values, actions) => {
          showLoading();
          getOtpOptions(email).then(({ response, error }) => {
            hideLoading();
            if (response) {
              otpOptions.current = response.data?.otpOptions;
              otpOptionsEncryptedPayload.current = response.data?.encryptedPayload;
              newPassword.current = values.newPassword;
              setShowPasswordEditOtpModal(true);
            } else {
              const errorMessage = handleApiError(error, "get password otp options");
              actions.setStatus([errorMessage]);
            }
          });
        }
      }
    >
      {({
        values,
        errors,
        touched,
        resetForm,
        handleChange,
        setFieldTouched,
        handleBlur,
        handleSubmit,
        isValid,
        setTouched,
        setErrors,
      }) => (
        <div className="col__12-12 u--paddingNone security-info-row">
          <form className="col__12-12 u--paddingNone-left u--paddingNone-right" onSubmit={handleSubmit}>
            <div className="security-info-column sui-grid sui-gap-2">

              <div className="col__12-12 u--paddingNone-left">
                <div className={classNames("form-input", {
                  "form-input--error": touched?.newPassword && !isEmpty(errors?.newPassword)
                })}>
                  <label className="form-input__label" htmlFor='newPassword'>
                    <span className="form-input__label-content">New Password</span>
                  </label>
                  <span className="sui-flex">
                    <Input
                      id="newPassword"
                      name="newPassword"
                      autoComplete="new-password"
                      valid={touched?.newPassword && !isEmpty(errors?.newPassword)}
                      onChange={(e) => {
                          try {
                            if (longestCommonPasswordSubstring(email, e.target.value)) {
                              setTouched({...touched, newPassword: true});
                              setErrors({
                                ...errors,
                                newPassword: [...errors.newPassword, MESSAGE.PASSWORD_CONTAINS_EMAIL]
                              });
                            }
                          } finally {
                            handleChange(e)
                          }
                        }
                      }
                      onBlur={(e) => {
                        if (!showPasswordEditOtpModal) {
                          handleBlur(e);
                          setIsNewPasswordFocus(false);
                        }
                      }}
                      type={showPassword ? "text" : "password"}
                      onFocus={onFocus}
                      value={values.newPassword}
                      autoFocus={!isNativeApp()} // eslint-disable-line
                      dataAutomationId='login-information-password-form--edit-password-field-new'
                    />
                    <label // eslint-disable-line
                      style={{marginLeft: (touched?.newPassword && !isEmpty(errors?.newPassword)) ? -74 : -48}}
                      id="showPasswordCheck"
                      className="u__text--alternate show-pass-transition"
                      onClick={() => setShowPassword(!showPassword)}
                      data-automation-id='login-information-password-form--edit-password-field-new_ShowPasswordButton'
                    >
                      {showPassword ? "Hide" : "Show"}
                    </label>
                  </span>
                  {touched.newPassword && !isEmpty(errors?.newPassword) && <FieldError name="newPassword" errors={errors?.newPassword} />}
                </div>
              </div>

              <CSSTransition
                in={ hasNewPasswordBeenFocused && (isNewPasswordFocus || !isEmpty(errors?.newPassword) ) }
                timeout={0}
                classNames="error"
                unmountOnExit
              >

                <PasswordRequirementsOutline password={ values.newPassword } email={email} />
              </CSSTransition>

              { !isUserB2B() && (
                <p className="new-password-reset-warning">
                  Resetting your password will sign you out of all other devices.
                </p>
              )}


              <ButtonGroup responsive>
                <Button variant='primary' type="submit" disabled={!isValid} data-automation-id='login-information-password-form--button-submit'>
                  Save Changes
                </Button>
                <Button onClick={onDoneEditing} data-automation-id='login-information-password-form--button-cancel'>
                  Cancel
                </Button>
              </ButtonGroup>
            </div>
          </form>
          { showPasswordEditOtpModal &&
            <PasswordEditOtpModal
              email={email}
              otpOptions={otpOptions.current}
              otpOptionsEncryptedPayload={otpOptionsEncryptedPayload.current}
              newPassword={newPassword.current}
              closeModal={closePasswordEditOtpModal}
              onSuccess={onDoneEditing}
              resetEditPasswordField={() => {
                resetForm();
                setFieldTouched("newPassword", true);
              }}
            />
          }
        </div>
      )}
    </Formik>
  );
}

export default PasswordEdit;